package com.neurobs.ccabserver.administrator.controller

import com.neurobs.ccab._
import com.neurobs.ccab.db.{AccountData, StudyTypeData, UserAccountViewData, UserData}
import com.neurobs.ccabserver.administrator.components.SessionConfigureDialog
import com.neurobs.js.view._
import com.neurobs.js.ccab.{CCABChatSession, CCABChatSessionNotify, CCABServer, ControllerConnectionStatus}
import com.neurobs.ccab.{CCABServer => CCABServerNew}
import com.neurobs.ccabserver.administrator.components.SessionConfigureDialog.{ManagedStartData, SetResumableSubjectGuids}
import com.neurobs.js.http.ServerObjectResponse
import com.neurobs.js.opentok.OpenTokChatSessionFactory
import com.neurobs.js.agora.AgoraChatSessionFactory
import com.neurobs.util.{ChatSessionFactory, CommunicationState, DataSocket, Logger, SecondsTimestamp}
import com.neurobs.ccabserver.administrator.view.{CommunicationResetView, ControllerSessionView, NoiseMetricsView, SequenceElementView, SessionControlController, TestLogTestData}
import io.circe.generic.auto._
import slinky.web.ReactDOM

import java.time.temporal.TemporalAmount
import scala.util.{Failure, Success}
import scala.concurrent.ExecutionContext.Implicits.global

class ControllerSessionController (user: AccountUserDataRest, studyTypes: Seq[StudyTypeData], studySessions: StudySessionManager, gui: Gui, val sessionInfo: ControllerSessionData, dataPassword: DataPasswordCache, examinerTags: ExaminerTagList, server: CCABServerNew)(implicit logger: Logger) {
	import MessageData._
	import com.neurobs.js.dom.DOMViewElement._

	println(s"controller version: ${sessionInfo.controllerVersion}")
	private val sender = new SendMessageAdapter
	private val chatDuringPause = sessionInfo.controllerVersion >= VersionNumber("0.003.003").get
	private val setSetAttenuation = sessionInfo.controllerVersion >= VersionNumber("0.003.004").get
	private val commResetSupported = sessionInfo.controllerVersion >= VersionNumber("0.004.000").get
	private val commentingSupported = sessionInfo.controllerVersion >= VersionNumber("0.004.000").get
	private val resumeSessionSupported = sessionInfo.controllerVersion >= VersionNumber("0.004.001").get
	private val dataSyncSupported = sessionInfo.controllerVersion >= VersionNumber("0.005.000").get
	private val tagCommentsSupported = sessionInfo.controllerVersion >= VersionNumber("0.005.000").get
	private val systemShutdownSupported = sessionInfo.controllerVersion >= VersionNumber("0.005.000").get
	private val testRunHistorySupported = sessionInfo.controllerVersion >= VersionNumber("0.006.000").get
	private val testMetadataSupported = sessionInfo.controllerVersion >= VersionNumber("0.006.001").get

	private val state: ControllerState = new ControllerState(new TestRunDetailCache(sender = sender, gui = gui, tags = examinerTags, useTagComments = tagCommentsSupported))
	private var isStopped = true
	private var lastHidden = false
	private var uploadCredentialsSent =  sessionInfo.controllerVersion < VersionNumber("0.006.000").get
	private var chatVolume: Option[Double] = None

	private var comState: CommunicationState = CommunicationState()
	private var latestNoiseData: Option[NoiseMetricsView.TestNoiseMetrics] = None

	private val dataSyncStateSource: Option[DataSyncStateSource] = {
		if (dataSyncSupported) Some(new DataSyncStateSource {
			override def requestUpdate(reset: Boolean): Unit = sender.send(GetDataSyncStateMessage(data = GetDataSyncStateData(reset)))
			override def setPaused(paused: Boolean): Unit = {
				ensureUploadCredentials { () =>
					sender.send(SetDataSyncPauseMessage(data = SetDataSyncPauseData(paused)))
				}
			}
			override def addBacklog(): Unit = {
				ensureUploadCredentials { () =>
					sender.send(MessageType.AddDataSyncBacklog)
				}
			}
		})
		else None
	}

	private var chatController: Option[VideoChatController] = None

	private val view = new ControllerSessionView(dataSyncStateSource, logger, commentingSupported, gui, showSystemShutdown = systemShutdownSupported)
//	view.setRecurringEvent(intervalMs = 1000*60*10) { () =>
//		CCABServer.loginPing(_ => Unit)
//		true
//	}

	def container: Container = view.container

	private val testListController = new TestListController(view.testSession.sequenceList)

	var onClose: () => Unit = () => Unit

	private val testStation = new TestStationAdapter(sender)

	private val sessionComments = new ExaminerCommentController(testLogIndex = None, delegate = new MessageExaminerCommentControllerDelegate(sender), gui = gui, tags = examinerTags, useTagComments = tagCommentsSupported)
	view.testSession.commentsContainer.addChild(sessionComments.container)

	private val sessionControlController = new SessionControlController(testStation, view.testSession.controlView, gui)

	private val sessionConfigureDispatch = SessionConfigureDialog.DispatchHolder()
	ReactDOM.render(SessionConfigureDialog.View(
		SessionConfigureDialog.Resources(
			server = server,
			adminUser = user,
			studyTypes = studyTypes,
			stationName = sessionInfo.controllerName,
			stationVersion = sessionInfo.controllerVersion,
			dataPasswordCache = dataPassword,
			studySessions = studySessions,
			logger = logger
		),
		sessionConfigureDispatch,
		onResumeSession = (data, history) => {
			ensureUploadCredentials { () =>
				sender.send(ResumeManagedSettingsMessage(data = data))
				if (testRunHistorySupported) {
					history.grouped(20).zipWithIndex.foreach { case (group, index) =>
						sender.send(SetTestRunHistoryMessage(data = SetTestRunHistoryData(append = index > 0, history = group)))
					}
				}
				if (testMetadataSupported) {
					sender.send(SetTestMetadataMessage(data = SetTestMetadataData(studySessions.config)))
				}
			}
		},
		onStart = data => {
			ensureUploadCredentials { () =>
				data.session match {
					case None => sender.send(SetSessionMessage(data = data.setData))
					case Some(ManagedStartData(m, identity_data, history)) =>
						sender.send(SetManagedSettingsMessage(data = SetManagedSessionData(
							test_session_id = m.session.id,
							start_time = m.session.startTime,
							study_name = m.studyName,
							access_guid = m.session.accessGuid,
							subject_guid = m.session.subjectGuid,
							reporting_id = m.reportingId,
							account_subject_id = m.session.accountSubjectId,
							settings = data.setData,
							metadata = data.metadata,
							identity =  identity_data.map(SubjectPropertiesData(_))
						)))
						if (testRunHistorySupported) {
							history.grouped(20).zipWithIndex.foreach { case (group, index) =>
								sender.send(SetTestRunHistoryMessage(data = SetTestRunHistoryData(append = index > 0, history = group)))
							}
						}
						if (testMetadataSupported) {
							sender.send(SetTestMetadataMessage(data = SetTestMetadataData(studySessions.config)))
						}
				}
			}
		}
	), view.sessionSelectionNew)

	//private def logTestSelect(i: Int): Unit = {
	//	view.testSession.log.setSelection(Some(i))
	//	update(false)
	//}
	//view.testSession.log.onSelect = logTestSelect

	private def runSelectedSequenceTest(args: SequenceElementView.RunArguments): Unit = {
		val startData = StartTestData(test_index = state.testIndex(args.test).get, start_value = args.start, sequence_id = Some(args.elementId), update_seq_pos = args.resetSequencePosition)
		sender.send(StartTestMessage(data = startData))
		view.testSession.sequenceElement.onCloseClicked()
	}
	//view.testSession.sequenceElement.onRun = runSelectedSequenceTest
	view.testSession.sequenceElement.onCloseClicked = { () =>
		view.testSession.setSequenceElementVisible(false)
		testListController.selectTest(None)
	}
	testListController.onTestSelectChange = {
		case None => view.testSession.setSequenceElementVisible(false)
		case Some(e) =>
			if (view.testSession.primarySequenceId != Some(e._1.id)) {
				if (state.stopped) {
					view.testSession.setTestVisible(visible = false)
				}
				val details = state.getRunsOfSequenceItem(e._1.id)
				view.testSession.sequenceElement.update(Some(e._1), e._2, details, state.stopped && (!comState.localAV), true, testStation, showComments = commentingSupported, firstDisplay = view.testSession.secondarySequenceId != Some(e._1.id))
				view.testSession.setSequenceElementVisible(true)
				lastHidden = true
			}
			else view.testSession.setSequenceElementVisible(false)
	}

	private def update(selectLatestTest: Boolean): Unit = {
		logger.debug("update()")
		view.videoChat.commStatus.update(volume = chatVolume, volumeControls = state.stopped)
		state.state.foreach { s =>
			if (s.help_req)
				view.setConnectionStatus(Some("Help Requested"), ControllerSessionView.StatusDisplayType.Attention)
			else if (s.con.status == PresentationConnectionStatus.Disconnected)
				view.setConnectionStatus(Some(s.con.error.map("Error with test computer: " + _).getOrElse("Test Server Disconnected")), ControllerSessionView.StatusDisplayType.Error)
			else view.setConnectionStatus(None)
		}

		val alertType = state.state.flatMap(_.session.map {s =>
			if (s.stopped) ControllerSessionView.AlertIndicatorType.Alert
			else ControllerSessionView.AlertIndicatorType.None
		}).getOrElse(ControllerSessionView.AlertIndicatorType.None)
		view.setAlertType(alertType)

		chatController foreach { c =>
			c.update(comState, state.sequenceStatus, state.state.flatMap(_.con.localPresentation))
		}

		view.setSessionSelectionVisible(state.showSessionSelect)

		val showSession = state.showSession
		view.setTestSessionVisible(showSession)
		if (showSession) {
			val session = state.session.get

			session.state.last_log foreach { log =>
//				val nse = noiseTestIndex match {
//					case 0 => NoiseMetrics(pwr = -60, snr = 100)
//					case 1 => NoiseMetrics(pwr = -60, snr = 98)
//					case 2 => NoiseMetrics(pwr = -40, snr = 100)
//					case 3 => NoiseMetrics(pwr = -40, snr = 98)
//				}
//				noiseTestIndex = (noiseTestIndex + 1) % 4

				log.nse foreach { nse =>
					val testName = session.testName(log.run.test)
					val newValue = NoiseMetricsView.TestNoiseMetrics(testName, nse)
					if (!latestNoiseData.contains(newValue)) {
						latestNoiseData = Some(newValue)
						view.videoChat.setNoiseMetrics(Some(newValue))
					}
				}
			}

			for(comments <- session.state.comments; tags <- session.state.tags) sessionComments.update(comments, tags)

			sessionControlController.update(session, comState.localAV)

			view.testSession.updateInfoTable(session.data, session.state, sessionInfo.caasType.toString)
			view.testSession.setPauseBetweenTestsVisible(session.state.pause_between.isDefined)
			session.state.pause_between.foreach(pause => view.testSession.pauseBetweenTestsCheck.setChecked(pause))

			testListController.setTests(session.sequence, session.data.group, session.state.last_log, session.state.log)

			//view.testSession.log.setElements(session.state.log.map(r => TestLogTestData(name = session.testName(r.test), hadError = r.error.isDefined)), session.sequence.nextTest(session.data.group).map(_.name))
			//if (selectLatestTest && session.state.log.nonEmpty) {
			//	view.testSession.log.setSelection(Some(session.state.log.length - 1))
			//	view.testSession.log.showLatestTest()
			//}
			//view.testSession.log.selection.flatMap(state.getTestDetail(_, sender)) match {
			//	case None => view.testSession.setTestVisible(false)
			//	case Some(detail) =>
			if (!state.stopped) {
				lastHidden = false
			}

			val primarySequenceId = if (lastHidden || session.state.log.isEmpty) None else session.state.log.last.seq_id
			if (primarySequenceId.isDefined && primarySequenceId == view.testSession.secondarySequenceId) view.testSession.setSequenceElementVisible(false)

			if ( (!lastHidden) && session.state.log.nonEmpty) {
				val last = session.state.log.last
				val test = session.data.group.tests(last.test)
				val details = last.seq_id match {
					case Some(id) => state.getRunsOfSequenceItem(id)
					case None =>
						val logIndex = session.state.log.length - 1
						List(state.testRunCache.get(logIndex))
				}
				//println(s"primarySequenceId: ${view.testSession.primarySequenceId}")
				//println(s"last id: ${last.seq_id}")
				view.testSession.test.update(item = last.seq_id.map(id => SequenceElement(id = id, index = last.test)), test = test, details = details, canRun = false, canClose = false, adapter = testStation, showComments = commentingSupported, firstDisplay = view.testSession.primarySequenceId != last.seq_id)
				view.testSession.setTestVisible(visible = true)
			}
			else view.testSession.setTestVisible(visible = false)

			testListController.selection match {
				case None => view.testSession.setSequenceElementVisible(false)
				case Some(e) =>
					if (primarySequenceId == Some(e._1.id)) view.testSession.setSequenceElementVisible(false)
					else {
						val details = state.getRunsOfSequenceItem(e._1.id)
						view.testSession.sequenceElement.update(Some(e._1), e._2, details, session.stopped && (!comState.localAV), true, testStation, showComments = commentingSupported, firstDisplay = view.testSession.secondarySequenceId != Some(e._1.id))
				}
			}

			view.testSession.setButtonStates(session.sequenceStatus)
		}

		val newIsStopped = state.stopped
		if (newIsStopped != isStopped) {
			isStopped = newIsStopped
			if (isStopped) gui.sounds.playAlert
		}
	}

//	private def RerunTest(test: Test, detail: TestRunDetail): Unit = {
//		val start = if (test.starts.isEmpty) None else
//			view.testSession.test.startSelect.value.flatMap(v => if (test.starts.contains(v)) Some(v) else None)
//
//		val data = StartTestData(test_index = detail.run.test, start_value = start, sequence_id = detail.run.seq_id, update_seq_pos = false)
//		sender.send(StartTestMessage(data = data))
//	}

	private def processMessage(msg: MessageData): Unit = msg match {
		case data: TestPackageDataMessage =>
			if (state.setTestPackage(data.data)) {
				sessionConfigureDispatch.send(SessionConfigureDialog.SetPackageData(Some(data.data)))
				update(false)
			}
		case commandError: CommandErrorMessage =>
			view.addError(commandError.data.error)
		case error: NotificationMessage =>
			view.addError(error.data.message)
		case state: CommStateMessage =>
			view.videoChat.commStatus.update(state.data, volumeControls = this.state.stopped)
		case vol: ChatVolumeMessage =>
			chatVolume = Some(vol.data.volume)
			view.videoChat.commStatus.update(volume = chatVolume, volumeControls = this.state.stopped)
		case state: PowerStatusMessage =>
			chatController.foreach(_.updatePowerStatus(state.data))
		case s: SessionDataMessage =>
			if (state.setSessionData(s.data)) update(true)
		case s: StateDataMessage =>
			chatVolume = s.data.chat_vol
			val changes = state.setState(s.data, sender)
			update(changes.newRun)
		case d: TestDetailMessage =>
			state.testRunCache.set(d.data.log_index, d.data.data)
			update(false)
		case d: PerformanceDataMessage =>
			state.testRunCache.set(d.data.log_index, d.data.data)
			update(false)
		case d: DataSyncStateMessage =>
			dataSyncStateSource.foreach(_.applyUpdate(d.data))
		case d: ResumableSessionsMessage =>
			sessionConfigureDispatch.send(SetResumableSubjectGuids(d.data.subject_guids))
		case _: MessageData =>
	}

	private def start(socket: CCABChatSession): Unit = {
		chatController = Some(new VideoChatController(view.videoChat, socket, chatDuringPause = chatDuringPause))
		chatController.get.setExaminerList(socket.examinerNames)
		view.setConnectionStatus(None)
		sender.resetSender(socket.sendAdminMessage(_))
		sender.send(MessageType.GetTestPackageData)
		sender.send(MessageType.GetState)
		sender.send(MessageType.GetCommState)
		sender.send(MessageType.GetPowerStatus)
		dataSyncStateSource.foreach(_.requestUpdate(reset = true))
		if (resumeSessionSupported) {
			sender.send(GetResumableSessionsMessage(data = GetResumableSessionsData(minimum_start_time = SecondsTimestamp(SecondsTimestamp.now().instant.minusSeconds(60 * 60 * 12)))))
		}
		view.closeLink.setHandler { () =>
			socket.close()
			onClose()
		}
		view.connectionLink.setHandler { () =>
			CommunicationResetView.show(gui, socket, commResetSupported)
		}
		view.setVideoChatVisible(true)
	}

	private def ensureUploadCredentials(action: () => Unit): Unit = {
		//println(s"uploadCredentialsSent: $uploadCredentialsSent")
		if (uploadCredentialsSent) action()
		else {
			server.getTestStationSyncData("dummy_id")._2 map {
				case Left(error) =>
					view.addError(s"Error retrieving data syncing configuration: ${error.description}")
				case Right(data) =>
					sender.send(SetUploadCredentialsMessage(data = SetUploadCredentialsData(access_key = data.accessKey, secret_key = data.secretKey)))
					// This is a temporary hack to keep VA data from syncing
					if (user.account.id == 3) {
						dataSyncStateSource.foreach(_.setPaused(true))
					}
					else {
						dataSyncStateSource.foreach(_.addBacklog())
					}
					uploadCredentialsSent = true
					action()
			}
		}
	}

	private def onConnectionClose(error: String): Unit = {
		view.setConnectionStatus(Some(error))
		view.closeLink.setHandler(() => onClose())
	}

	private def onFatalError(error: String): Unit = {
		logger.error(error)
		onConnectionClose(error)
	}

	private def connect(data: ControllerSessionTokenData): Unit = {
		var session: CCABChatSession = null
		val delegate = new CCABChatSessionNotify {
			var connectedOnce = false
			override def connectionStatusChanged(newStatus: ControllerConnectionStatus.Value, error: Option[String]): Unit = {
				val error_suffix = error.map(": " + _).getOrElse("")
				val status = newStatus match {
					case ControllerConnectionStatus.Connected =>
						if (!connectedOnce) {
							connectedOnce = true
							start(session)
						}
						else view.setConnectionStatus(None)
					case ControllerConnectionStatus.Disconnected => view.setConnectionStatus(Some("Test Station Disconnected" + error_suffix))
					case ControllerConnectionStatus.Closed => onConnectionClose(error = "Closed"+ error_suffix)
				}
			}

			override def communicationStateChanged(newState: CommunicationState): Unit = {
				comState = newState
				update(selectLatestTest = false)
			}

			override def publishAVError(error: String): Unit = {
				logger.error("CCABChatSessionNotify.publishAVError: " + error)
				gui.showModalMessage(width = 30, title = "Chat Error", description = s"There was an error starting AV transmission to the test system:\n\n$error", dismissButtonCaption = "Close")()
			}

			override def subscribeToRemoteError(error: String): Unit = {
				logger.error("CCABChatSessionNotify.subscribeToRemoteError: " + error)
				gui.showModalMessage(width = 30, title = "Chat Error", description = s"There was an error receiving AV transmission from the test system:\n\n$error", dismissButtonCaption = "Close")()
			}

			override def genericError(error: String): Unit = {
				logger.error("CCABChatSessionNotify.genericError: " + error)
				view.addError(error)
			}

			override def controllerMessageReceived(message: MessageData): Unit = processMessage(message)

			override def examinerListChanged(examiners: Seq[String]): Unit = {
				logger.debug(s"Examiners: $examiners")
				//gui.sounds.playNotify()
				chatController foreach { c =>
					c.setExaminerList(examiners)
				}
			}

			override def examinerChat(message: String, name: String): Unit = {
				logger.debug(s"Chat $name: $message")
				gui.sounds.playNotify()
				chatController foreach { c =>
					c.addExaminerMessage(message, name)
				}
			}
		}

		var factory : Option[ChatSessionFactory] = None
		if(data.opentokToken != None) {
			factory = Some(new OpenTokChatSessionFactory(data.opentokToken.getOrElse(new OpentokTokenData(0,"",""))))
		} else if(data.agoraToken != None) {
			factory = Some(new AgoraChatSessionFactory(data.agoraToken.getOrElse(new AgoraTokenData("","","", "", ""))))
		} else {
			onFatalError(error = "No video chat token has been set")
		}



		factory foreach { f =>
			CCABChatSession.connect(f, data.clientId.get, delegate, view.videoChat.localVideo, view.videoChat.remoteVideo, view.videoChat.remoteAdminVideo) match {
				case Right(s) => session = s
				case Left(e) => onFatalError(error = e)
			}
		}
	}

	private def handleToken(response: ServerObjectResponse[ControllerSessionTokenData]): Unit = {
		response match {
			case Left(error) => onFatalError(error.message)
			case Right(tokenData) =>
				tokenData.error match {
					case Some(error) => onFatalError(error)
					case None => connect(tokenData)
				}
		}
	}

	view.setTitle(sessionInfo.controllerName)
	view.setConnectionStatus(Some("Connecting..."), ControllerSessionView.StatusDisplayType.Info)
	view.testSession.pauseBetweenTestsCheck.setChangeHandler { () =>
		state.session.foreach{ s =>
			if (s.state.pause_between.forall(_ != view.testSession.pauseBetweenTestsCheck.checked))
				sender.send(if (view.testSession.pauseBetweenTestsCheck.checked) MessageType.SetPauseBetweenTrue else MessageType.SetPauseBetweenFalse)
		}
	}
	view.setTestSessionVisible(false)
	view.setSessionSelectionVisible(false)
	view.testSession.endSessionButton.setHandler { () =>
		gui.showConfirmationDialog(title = "End Session Confirmation", yesNoQuestion = "Do you want to end the current test session?") { () =>
			sender.send(MessageType.EndSession)
			sessionConfigureDispatch.send(SessionConfigureDialog.Clear)
			if (resumeSessionSupported) {
				sender.send(GetResumableSessionsMessage(data = GetResumableSessionsData(minimum_start_time = SecondsTimestamp(SecondsTimestamp.now().instant.minusSeconds(60 * 60 * 12)))))
			}
			testListController.clear()
			latestNoiseData = None
		}
	}
//	view.shutdownAppLink.setHandler { () =>
//		gui.showConfirmationDialog(title = "Close Test Station Application", yesNoQuestion = "Do you want to close the Test Station application? Note that this will not turn off the computer.") { () =>
//			testStation.shutdownTestStationApp()
//		}
//	}
	view.shutdownSystemLink.setHandler { () =>
		gui.showConfirmationDialog(title = "Turn Off Test Station Computer", yesNoQuestion = "Do you want to turn off the Test System?") { () =>
			testStation.shutdownTestStationSystem()
		}
	}
	view.closeLink.setHandler { () => onClose() }
	CCABServer.getControllerSessionToken(sessionInfo.sessionId)(handleToken)
}
